import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, HeartHandshake, Heart, BookOpen, AlertCircle, Users, Globe, Power, History, Mail } from "lucide-react";
import { useSEO } from "../lib/seo";

function Section({ icon: Icon, title, children }) {
  return (
    <section className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-light)] p-5">
      <h2 className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[1.5px] text-gold">
        <Icon size={14} /> {title}
      </h2>
      <div className="mt-3 text-sm text-text leading-relaxed">{children}</div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gold" />
      <span>{children}</span>
    </li>
  );
}

export default function TermsOfUse() {
  const navigate = useNavigate();
  useSEO({ title: "Terms of Use", description: "GurSetu Terms of Use", path: "/terms" });

  return (
    <div className="min-h-screen bg-app">
      <header className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 border-b border-[var(--glass-border)] glass">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full grid place-items-center btn-ghost" aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-heading text-lg font-extrabold text-text">Terms of Use</h1>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-6 space-y-4 pb-28">
        <Section icon={HeartHandshake} title="Welcome to GurSetu">
          <p>GurSetu is a spiritual companion app created with love and devotion. By using this app, you agree to these simple terms. Think of them as maryada for using our digital sangat.</p>
        </Section>

        <Section icon={Heart} title="1. Seva, Not Service">
          <p>GurSetu is provided as a free seva (service) to the Panth. We do not charge for any features, and we never will. This app is our humble offering to Sri Guru Granth Sahib Ji.</p>
        </Section>

        <Section icon={BookOpen} title="2. Sacred Content">
          <p className="mb-2">All Gurbani content is sourced from BaniDB and is the property of the Sikh community. We request you to:</p>
          <ul className="space-y-1.5">
            <Bullet>Treat all Gurbani with utmost respect</Bullet>
            <Bullet>Use the content for spiritual growth only</Bullet>
            <Bullet>Not misuse or misrepresent any teachings</Bullet>
            <Bullet>Maintain proper maryada while using the app</Bullet>
          </ul>
        </Section>

        <Section icon={AlertCircle} title="3. No Warranties">
          <p>While we strive for accuracy, GurSetu is provided "as is" without warranties. For religious guidance, always consult with your local Gurudwara Sahib and learned Singhs/kaurs.</p>
        </Section>

        <Section icon={Users} title="4. Community Guidelines">
          <p className="mb-2">If any feature allows community interaction, you agree to:</p>
          <ul className="space-y-1.5">
            <Bullet>Be respectful to all users</Bullet>
            <Bullet>Not post inappropriate content</Bullet>
            <Bullet>Follow Sikh maryada in all interactions</Bullet>
            <Bullet>Report any violations to our team</Bullet>
          </ul>
        </Section>

        <Section icon={Globe} title="5. Intellectual Property">
          <p>The GurSetu name, logo, and design are property of Codesphere Technologies. The app is open source — we encourage you to contribute and improve it for the entire Panth.</p>
        </Section>

        <Section icon={Power} title="6. Termination">
          <p>You may stop using GurSetu at any time by uninstalling the app. We reserve the right to restrict access if these terms are violated, though we hope it never comes to that.</p>
        </Section>

        <Section icon={History} title="7. Changes to Terms">
          <p>We may update these terms occasionally. Continued use of the app after changes constitutes acceptance of the new terms. We'll notify you of any significant changes in-app.</p>
        </Section>

        <Section icon={Mail} title="Questions?">
          <p className="mb-1">We're here to help:</p>
          <a href="mailto:support@gursetu.app" className="inline-flex items-center gap-2 font-semibold text-gold">
            📧 support@gursetu.app
          </a>
        </Section>

        <p className="pt-2 text-center text-xs italic text-gold">May Waheguru bless your journey. 🙏</p>
      </div>
    </div>
  );
}